import { Campaign } from "../types/Campaigns";
import { HttpError } from "../types/Error";
import { PagedResults } from "../types/PagedResults";
import { UpdatedUser, User, UserSummary } from "../types/User";
import useApiClient from "./ApiClient";

const useUserService = () => {
    const { get, post, put, delete: del } = useApiClient();

    const getUser = async (userId: string): Promise<User> => {
        try {
            const result = await get<User>(`/users/${userId}`);
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
            
            return result as User;
        } catch (error) {
            console.error('Error getting user:', error);
            throw error;
        }
    }

    const searchUsers = async (searchTerm?:string, page?:number, orderBy?:string, orderDirection?:string): Promise<PagedResults<UserSummary>> => {
        try {

            const postData = {
                searchTerm,
                page: page ?? 1,
                itemsPerPage: 10,
                orderBy: orderBy ?? "name",
                orderDirection: orderDirection ?? "asc"
            };

            const result = await post<PagedResults<UserSummary>>(`/users/search`, postData);
            
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
            
            return result as PagedResults<UserSummary>;

        } catch (error) {
            console.error('Error searching users:', error);
            throw error;
        }
    }

    /**
     * Grants the user a free pro subscription. Pass an expiry to make it a trial that lapses on its
     * own; leave it out to grant the subscription indefinitely.
     */
    const enableGiftedSubscription = async (userId: string, expiresAt?: Date | null): Promise<void> => {
        try {
            await post(`/users/${userId}/giftedsubscription`, { expiresAt: expiresAt ? expiresAt.toISOString() : null });
        } catch (error) {
            console.error('Error enabling gifted subscription for user:', error);
            throw error;
        }
    }

    const disableGiftedSubscription = async (userId: string): Promise<void> => {
        try {
            await del(`/users/${userId}/giftedsubscription`);
        } catch (error) {
            console.error('Error disabling gifted subscription for user:', error);
            throw error;
        }
    }

    const updateUser = async (user: UpdatedUser): Promise<User> => {
        try {
            const result = await put<User>(`/users/${user.id}`, user);
            // Check if the response is an error.
            if ((result as HttpError).errorCode) {
                throw (result as HttpError).description;
            }
            
            return result as User;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    const getCampaignPreviewsEnabledForUser = async (userId: string): Promise<boolean> => {
        try {
            const result = await get(`/users/${userId}/campaignpreviews`);
            // Result is expected to just be "true" or "false".
            return result;
        } catch (error) {
            console.error('Error getting campaign previews enabled for user:', error);
            throw error;
        }
    }

    const enableCampaignPreviewsForUser = async (userId: string): Promise<void> => {
        try {
            await post(`/users/${userId}/campaignpreviews`, {  });
        } catch (error) {
            console.error('Error enabling campaign previews for user:', error);
            throw error;
        }
    }

    const disableCampaignPreviewsForUser = async (userId: string): Promise<void> => {
        try {
            await del(`/users/${userId}/campaignpreviews`);
        } catch (error) {
            console.error('Error disabling campaign previews for user:', error);
            throw error;
        }
    }

    const getPreviewCampaignsForUser = async (userId: string): Promise<Campaign[]> => {
        try {
            const result = await get<Campaign[]>(`/users/${userId}/previewcampaigns`);
            // Check if the response is an error.
            if ((result as unknown as HttpError).errorCode) {
                throw (result as unknown as HttpError).description;
            }

            return result as Campaign[];
        } catch (error) {
            console.error('Error getting the campaigns a user can preview:', error);
            throw error;
        }
    }

    return {getUser, searchUsers, updateUser, getCampaignPreviewsEnabledForUser, enableCampaignPreviewsForUser, disableCampaignPreviewsForUser, getPreviewCampaignsForUser, enableGiftedSubscription, disableGiftedSubscription};
}

export default useUserService;